import * as React from "react";
import {
  List,
  DataTable,
  NumberField,
  DateField,
  FunctionField,
  Pagination,
  DateTimeInput,
  useListContext,
  Button,
  downloadCSV,
  ArrayField,
  SingleFieldList,
  ChipField,
  useRedirect,
} from "react-admin";
import { addHours, endOfDay, startOfDay, subDays } from "date-fns";
import { getPurchaseStatusName } from "../utils/PurchaseUtils";
import {
  DATE_TIME_FIELD_OPTIONS,
  DATE_TIME_LOCALE,
  formatDateFromISODate,
} from "../utils/DateTimeUtils";
import jsonExport from "jsonexport/dist";
import { formatNumberToPrice } from "../utils/NumberUtils";
import { Box, Grid, Card, CardContent, Typography } from "@mui/material";

const defaultPurchaseDateFilters = () => {
  const now = new Date();
  const isBetweenMidnightAnd3am = now.getHours() >= 0 && now.getHours() <= 3;
  if (!isBetweenMidnightAnd3am) {
    return {
      paidDateFrom: startOfDay(now),
      paidDateTo: endOfDay(now),
    };
  }

  return {
    paidDateFrom: addHours(startOfDay(subDays(now, 1)), 8),
    paidDateTo: now,
  };
};

const ResetFiltersButton = () => {
  const { setFilters } = useListContext();

  const handleClick = () => {
    setFilters(defaultPurchaseDateFilters(), [], true);
  };

  return <Button label="Restablecer Filtros" onClick={handleClick} />;
};

const PurchaseFilter = () => {
  return [
    <DateTimeInput source="paidDateFrom" label="Fecha Pago Desde" alwaysOn />,
    <DateTimeInput source="paidDateTo" label="Fecha Pago Hasta" alwaysOn />,
    <ResetFiltersButton alwaysOn />,
  ];
};

const PurchasesTotal = () => {
  const { data, isPending } = useListContext();

  if (isPending) return null;

  const purchasesTotalRestaurant = formatNumberToPrice(
    data?.[0]?.totalRestaurantFee
  );
  const purchasesTotalMorfy = formatNumberToPrice(data?.[0]?.totalMorfyFee);
  const purchasesTotalPaid = formatNumberToPrice(
    data?.[0]?.totalRestaurantFee + data?.[0]?.totalMorfyFee
  );

  return (
    <Box sx={{ m: 2 }}>
      <Grid container spacing={2} sx={{ justifyContent: "center" }}>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <Typography color="textSecondary" gutterBottom>
                Total Vendido
              </Typography>
              <Typography variant="h5" component="div" color="info.main">
                {purchasesTotalPaid}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <Typography color="textSecondary" gutterBottom>
                Total Ingreso Restaurante
              </Typography>
              <Typography variant="h5" component="div" color="success.main">
                {purchasesTotalRestaurant}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <Typography color="textSecondary" gutterBottom>
                Total Comisión Morfy
              </Typography>
              <Typography variant="h5" component="div" color="primary">
                {purchasesTotalMorfy}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

const restaurantExporter = (purchases) => {
  const purchasesForExport = purchases.map((purchase) => {
    const purchaseForExport = {
      Productos: purchase.productsData
        ?.map((product) => product.name)
        .join("|"),
      Estado: getPurchaseStatusName(purchase.status),
      "Fecha de Pago": formatDateFromISODate(purchase.paidDate),
      "Fecha de Entrega": formatDateFromISODate(
        purchase?.deliveredPurchase?.deliveredDate
      ),
      Pagado: purchase.totals?.paidAmount,
      "Ingreso Restaurante": purchase.payment?.restaurantSplitAmount,
      "Comisión Morfy": purchase.payment?.morfySplitAmount,
    };
    return purchaseForExport;
  });

  jsonExport(
    purchasesForExport,
    {
      headers: [
        "Productos",
        "Estado",
        "Fecha de Pago",
        "Fecha de Entrega",
        "Pagado",
        "Ingreso Restaurante",
        "Comisión Morfy",
      ],
    },
    (err, csv) => {
      downloadCSV(csv, `activity_${new Date().toISOString().split("T")[0]}`);
    }
  );
};

const PurchaseActivityList = () => {
  const redirect = useRedirect();

  const onShowClick = (id) => {
    if (!id) return;
    redirect("show", "/purchases", id);
  };

  return (
    <List
      title="Actividad"
      filters={PurchaseFilter()}
      filterDefaultValues={defaultPurchaseDateFilters()}
      filter={{ populateTotalFee: true }}
      pagination={<Pagination />}
      exporter={restaurantExporter}
    >
      <DataTable rowClick={onShowClick} bulkActionButtons={false}>
        <DataTable.Col label="Producto(s)" disableSort>
          <ArrayField label="Producto(s)" source="productsData">
            <SingleFieldList linkType={false}>
              <ChipField source="name" size="small" />
            </SingleFieldList>
          </ArrayField>
        </DataTable.Col>
        <DataTable.Col source="status" label="Estado">
          <FunctionField
            source="status"
            label="Estado"
            render={(record) => getPurchaseStatusName(record.status)}
          />
        </DataTable.Col>
        <DataTable.Col source="paidDate" label="Fecha de Pago">
          <DateField
            source="paidDate"
            label="Fecha de Pago"
            showTime={true}
            locales={DATE_TIME_LOCALE}
            options={DATE_TIME_FIELD_OPTIONS}
            emptyText="-"
          />
        </DataTable.Col>
        <DataTable.Col
          source="deliveredPurchase.deliveredDate"
          label="Fecha de Entrega"
        >
          <DateField
            source="deliveredPurchase.deliveredDate"
            label="Fecha de Entrega"
            showTime={true}
            locales={DATE_TIME_LOCALE}
            options={DATE_TIME_FIELD_OPTIONS}
            emptyText="-"
          />
        </DataTable.Col>
        <DataTable.Col label="Pagado" disableSort>
          <NumberField
            source="totals.paidAmount"
            label="Pagado"
            options={{
              style: "currency",
              currency: "ARS",
              currencyDisplay: "narrowSymbol",
            }}
          />
        </DataTable.Col>
        <DataTable.Col label="Ingreso Restaurante" disableSort>
          <NumberField
            source="payment.restaurantSplitAmount"
            label="Ingreso Restaurante"
            options={{
              style: "currency",
              currency: "ARS",
              currencyDisplay: "narrowSymbol",
            }}
          />
        </DataTable.Col>
        <DataTable.Col label="Comisión Morfy" disableSort>
          <NumberField
            source="payment.morfySplitAmount"
            label="Comisión Morfy"
            options={{
              style: "currency",
              currency: "ARS",
              currencyDisplay: "narrowSymbol",
            }}
          />
        </DataTable.Col>
      </DataTable>
      <PurchasesTotal />
    </List>
  );
};

export default PurchaseActivityList;
